import AddEditDataAgentUIForm from './AddEditDataAgentUIForm';
import {
  React,
  useNavigate,
  FormikHelpers,
  Yup,
  useEffect,
  useLocation,
  useDispatch,
  showToaster,
  useState,
  CircularProgress,
  Box,
  Typography,
} from '../../utils/globalImports';
import {
  useCreateDataAgentMutation,
  useUpdateDataAgentMutation,
  useGetDataAgentByIdQuery,
} from '../../api/dataAgentApiSlice';
import { useGetCustomerByIdQuery, useGetAllCustomersQuery } from '../../api/customerApiSlice';
import { useParams } from 'react-router-dom';
import { RootState } from '../../store';
import { AccountResponse } from '../../store/types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DataAgentTabs from './DataAgentTabs';

// Define interface for form values
export interface DataAgentFormValues {
  name: string;
  customer_id: string;
  client_id: string;
  description: string;
  instructions: string;
  system_prompt: string;
  max_tokens: number;
  temperature: number;
  is_active: boolean;
  is_published: boolean;
  icon: string;
}

interface ExtendedAccountResponse extends AccountResponse {
  role?: string;
  customer_id?: string;
  client_id?: string;
}

const AddEditDataAgent: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dataAgentId } = useParams<{ dataAgentId: string }>();
  const { selectedCustomerId, selectedClientId } = useSelector((state: RootState) => state.vaultilake);
  const location = useLocation();
  const [createDataAgent] = useCreateDataAgentMutation();
  const [updateDataAgent] = useUpdateDataAgentMutation();

  // Get auth state
  const auth = useSelector((state: RootState) => state.auth);
  const account = auth.account as ExtendedAccountResponse | null;
  const userRole = (account?.user?.role || account?.role)?.trim().toLowerCase();
  const userCustomerId = account?.user?.customer_id || account?.customer_id || '';
  const userClientId = account?.user?.client_id || account?.client_id || '';

  // Get customer_id from query parameters
  const queryParams = new URLSearchParams(location.search);
  const queryCustomerId = queryParams.get('customer_id') || '';
  const queryCustomerName = queryParams.get('customer_name')
    ? decodeURIComponent(queryParams.get('customer_name')!)
    : '';

  // Determine mode: 'add' or 'edit'
  const isEditMode = !!dataAgentId;

  // Fetch data agent data for edit mode
  const {
    data: dataAgentData,
    isLoading: isDataAgentLoading,
    isError: isDataAgentError,
    isFetching: isDataAgentFetching,
    refetch: refetchDataAgent,
  } = useGetDataAgentByIdQuery(dataAgentId || '', {
    skip: !isEditMode,
  });

  // Handle different API response formats
  const dataAgent = dataAgentData;

  // Fetch customer list for dropdown (skip for customeradmin and clientadmin)
  const {
    data: customerListData,
    isLoading: isCustomerListLoading,
    isError: isCustomerListError,
  } = useGetAllCustomersQuery(
    {},
    {
      skip: userRole === 'customeradmin' || userRole === 'clientadmin',
    },
  );

  const customerList = customerListData?.customers || customerListData || [];

  // Determine customer ID to fetch
  const effectiveCustomerId = isEditMode
    ? dataAgent?.customer_id || ''
    : selectedCustomerId ||
      queryCustomerId ||
      (userRole === 'customeradmin' || userRole === 'clientadmin' ? userCustomerId : '');

  // Fetch customer data
  const {
    data: customerData,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
    error: customerError,
  } = useGetCustomerByIdQuery(effectiveCustomerId || '', {
    skip: !effectiveCustomerId || (userRole !== 'customeradmin' && userRole !== 'clientadmin' && !isEditMode),
  });

  // Ensure customerName is set correctly
  const customerName = customerData?.name || queryCustomerName || '';

  // Initial form values - SIMPLIFIED: Compute directly instead of using useState
  const initialValues: DataAgentFormValues =
    isEditMode && dataAgent
      ? {
          name: dataAgent.name || '',
          customer_id: dataAgent.customer_id || '',
          client_id: dataAgent.client_id || '',
          description: dataAgent.description || '',
          instructions: dataAgent.instructions || '',
          system_prompt: dataAgent.system_prompt || '',
          max_tokens: dataAgent.max_tokens ?? 0,
          temperature: dataAgent.temperature ?? 0,
          is_active: dataAgent.is_active ?? true,
          is_published: dataAgent.is_published ?? false,
          icon: dataAgent.icon || 'Database',
        }
      : {
          name: '',
          customer_id: effectiveCustomerId,
          client_id: selectedClientId || (userRole === 'clientadmin' ? userClientId : ''),
          description: '',
          instructions: '',
          system_prompt: '',
          max_tokens: 4000,
          temperature: 0.7,
          is_active: true,
          is_published: false,
          icon: 'Database',
        };

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(t('dataAgents.validation.nameRequired'))
      .max(100, t('dataAgents.validation.nameMaxLength')),
    customer_id: Yup.string().required(t('dataAgents.validation.customerRequired')),
    client_id: Yup.string().required(t('dataAgents.validation.clientRequired')),
    description: Yup.string().max(500, t('dataAgents.validation.descriptionMaxLength')),
    instructions: Yup.string().required(t('dataAgents.validation.instructionsRequired')),
    max_tokens: Yup.number()
      .required(t('dataAgents.validation.maxTokensRequired'))
      .integer(t('dataAgents.validation.maxTokensMustBeInteger'))
      .min(0, t('dataAgents.validation.minTokensMin'))
      .max(2147483647, t('dataAgents.validation.maxTokensMax')),
    temperature: Yup.number()
      .required(t('dataAgents.validation.temperatureRequired'))
      .min(0, t('dataAgents.validation.temperatureMin'))
      .max(2, t('dataAgents.validation.temperatureMax')),
    is_active: Yup.boolean(),
    is_published: Yup.boolean(),
    icon: Yup.string(),
  });

  const handleSubmit = async (
    values: DataAgentFormValues,
    { setFieldError, setSubmitting }: FormikHelpers<DataAgentFormValues>,
  ) => {
    try {
      const payload = {
        name: values.name.trim(),
        customer_id: values.customer_id,
        client_id: values.client_id,
        description: values.description.trim(),
        instructions: values.instructions.trim(),
        system_prompt: values.system_prompt.trim(),
        max_tokens: values.max_tokens,
        temperature: values.temperature,
        is_active: values.is_active,
        is_published: values.is_published,
        icon: values.icon,
      };

      if (isEditMode) {
        if (!dataAgentId) {
          throw new Error(t('dataAgents.errors.missingId'));
        }
        await updateDataAgent({
          data_agent_id: dataAgentId,
          ...payload,
        }).unwrap();
        dispatch(
          showToaster({
            message: t('dataAgents.messages.updateSuccess'),
            type: 'success',
            duration: 2500,
          }),
        );
      } else {
        await createDataAgent(payload).unwrap();
        dispatch(
          showToaster({
            message: t('dataAgents.messages.createSuccess'),
            type: 'success',
            duration: 2500,
          }),
        );
      }
      navigate('/data-agents/data-agent-management');
    } catch (err: any) {
      let errorMessage = t(isEditMode ? 'dataAgent.errors.updateFailed' : 'dataAgent.errors.createFailed');
      if (err.status === 400 && err.data?.message) {
        errorMessage = err.data.message;
        if (err.data.message.includes('name already exists')) {
          setFieldError('name', t('dataAgent.errors.nameExists'));
        } else if (err.data.message.includes('Missing required field')) {
          const field = err.data.message.match(/Missing required field: (\w+)/)?.[1];
          if (field) setFieldError(field, err.data.message);
        }
      } else if (err.status === 403) {
        errorMessage = t('dataAgent.errors.accessDenied');
      }
      dispatch(
        showToaster({
          message: errorMessage,
          type: 'error',
          duration: 4000,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state for edit mode until data agent data is fetched
  if (isEditMode && (isDataAgentLoading || !dataAgent)) {
    // <CircularProgress size={40} />;
    // return <div>{t('dataAgent.loading.dataAgentData')}</div>;
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="75vh" flexDirection="column" gap={2}>
        <CircularProgress size={40} />
        <Typography color="text.secondary">{t('dataAgent.loading.data')}</Typography>
      </Box>
    );
  }

  // Show loading state for add mode
  if (!isEditMode && isCustomerLoading && effectiveCustomerId) {
    // <CircularProgress size={40} />;
    // return <div>{t('dataAgent.loading.data')}</div>;
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="75vh" flexDirection="column" gap={2}>
        <CircularProgress size={40} />
        <Typography color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  // Show loading state for customer list
  if (!isEditMode && userRole === 'superadmin' && isCustomerListLoading) {
    // return <div>{t('dataAgent.loading.customerList')}</div>;
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="75vh" flexDirection="column" gap={2}>
        <CircularProgress size={40} />
        <Typography color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  // Type-safe error handling for customerError
  if ((isEditMode && (isDataAgentError || !dataAgentId)) || (!isEditMode && isCustomerError && effectiveCustomerId)) {
    let errorMessage = isEditMode
      ? t('dataAgent.errors.loadDataAgentFailed')
      : t('dataAgent.errors.loadCustomerFailed');

    if (isCustomerError && customerError && 'status' in customerError && typeof customerError.status === 'number') {
      errorMessage = t('dataAgent.errors.accessDeniedCustomer', { status: customerError.status });
    }

    return <div>{errorMessage}</div>;
  }

  // Error handling for customer list fetch
  if (!isEditMode && userRole === 'superadmin' && isCustomerListError) {
    return <div>{t('common.loadingError')}</div>;
  }

  return (
    <>
      <AddEditDataAgentUIForm
        entityType={isEditMode ? 'edit' : 'add'}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        dataAgentId={dataAgentId}
        customerData={customerData}
        customerName={customerName}
        customerList={customerList}
        customerLoading={isCustomerListLoading}
        onAutoConfigureComplete={isEditMode ? refetchDataAgent : undefined}
      />
    </>
  );
};

export default AddEditDataAgent;
